import { element } from "./dom.js";
import type { PartState } from "./part-state.js";
import {
  type FlowPart,
  type FlowRow,
  SheetFlow,
  sheetCue,
  sheetHanzi,
  sheetNumber,
  sheetRows,
} from "./sheet-flow.js";
import type { SentencePiece, SheetSentence } from "./sheet-sentences.js";
import type { GlyphLibrary } from "./worksheet-drawing.js";
import type { SheetLabels } from "./worksheet-page.js";
import { pageRoom, sized } from "./worksheet-page.js";
import { seededShuffle } from "./worksheet-seed.js";
import type { WorksheetPaper } from "./worksheet-state.js";

/**
 * The sentence sheet 句子, as pages ready to print.
 *
 * Four parts and the answers, each item one of the 例句 the reader chose from
 * a List, one of their own Lists or their saved sentences. **Fill the gaps**
 * 选词填空 takes a word out of its sentence and gives the words of five
 * sentences in a box above them. **Put in order** 连词成句 gives a sentence's
 * words out of order, over a square for each character. **Write the
 * characters** 看拼音写句子 gives a sentence's pinyin and English over the same
 * squares, a syllable to a square. Each of those has one answer, so its
 * squares can say how many characters it has. **Translate** 翻译 gives the
 * English and a word to use over a ruled line, since a sentence can be said
 * more than one way, and its key gives one of them.
 *
 * The sentences are dealt to the ticked parts in turn, so each is asked
 * once. A sentence's words are the ones the site cut it into, the same cut
 * the word chips under it on its page are drawn from.
 */

/** The parts a sentence sheet prints. */
export type SentencePart = "gaps" | "arrange" | "pinyin" | "translate" | "key";

/** The parts that ask something, in the order they print. */
const askingParts = ["gaps", "arrange", "pinyin", "translate"] as const;

/** A part that asks something. */
type AskingPart = (typeof askingParts)[number];

/** A sentence, and the word in it a gap or a translation asks about. */
export interface SentenceItem {
  readonly sentence: SheetSentence;
  readonly word: SentencePiece;

  /** Where the word starts in the sentence's text. */
  readonly at: number;
}

/** What the sheet asks of the chosen sentences. */
export type SentenceChoice = Readonly<
  Record<AskingPart, readonly SentenceItem[]>
>;

/** The fewest and most words a sentence to put in order has. */
const arrangeWords = { fewest: 3, most: 8 } as const;

const han = /\p{Script=Han}/u;

/**
 * Whether a sentence can be put in order: three to eight words, and no
 * punctuation inside it, since a comma would give away where a clause ends.
 */
export function arrangeable(sentence: SheetSentence): boolean {
  const letters = Array.from(sentence.text);
  const inside = letters
    .slice(0, -1)
    .some((letter) => !han.test(letter) && letter.trim() !== "");

  return (
    !inside &&
    sentence.pieces.length >= arrangeWords.fewest &&
    sentence.pieces.length <= arrangeWords.most
  );
}

/** How many 汉字 a piece of text holds. */
function hanCount(text: string): number {
  return Array.from(text).filter((letter) => han.test(letter)).length;
}

/** A word of a sentence, and where it starts. */
interface Placed {
  readonly word: SentencePiece;
  readonly at: number;
}

/** The first of these words with the most 汉字. */
function longest(found: readonly Placed[]): Placed | undefined {
  return found.reduce<Placed | undefined>(
    (best, one) =>
      best === undefined || hanCount(one.word.text) > hanCount(best.word.text)
        ? one
        : best,
    undefined,
  );
}

/**
 * The word a gap or a translation asks about, and where it starts.
 *
 * One of the sheet's own words where the sentence has one, so a List's sheet
 * asks about the List's words, then the longest word of two or more 汉字,
 * then the longest word. The first of those it finds, read left to right.
 * @param words - The words the sentence came with, from its List.
 */
export function focusWord(
  sentence: SheetSentence,
  words: ReadonlySet<string>,
): Placed | undefined {
  let from = 0;
  const placed = sentence.pieces.flatMap((piece) => {
    const at = sentence.text.indexOf(piece.text, from);
    if (at < 0) {
      return [];
    }
    from = at + piece.text.length;

    return [{ word: piece, at }];
  });
  return (
    longest(placed.filter((one) => words.has(one.word.text))) ??
    longest(placed.filter((one) => hanCount(one.word.text) >= 2)) ??
    longest(placed.filter((one) => hanCount(one.word.text) >= 1))
  );
}

/**
 * What to ask of these sentences, in the parts the sheet prints.
 *
 * The sentences are dealt to the ticked parts in turn, in the order they
 * print, so a sheet of twenty sentences and two parts asks ten in each. A
 * sentence that cannot be put in order passes that part to the next.
 * @param sentences - The sentences the reader chose, in order.
 * @param words - The words of the List they came from, if any.
 */
export function sentenceChoice(
  sentences: readonly SheetSentence[],
  words: readonly string[],
  state: Pick<PartState<SentencePart>, AskingPart>,
): SentenceChoice {
  const ticked = askingParts.filter((part) => state[part]);
  const known = new Set(words);
  const dealt: Record<AskingPart, SentenceItem[]> = {
    gaps: [],
    arrange: [],
    pinyin: [],
    translate: [],
  };
  let turn = 0;
  for (const sentence of sentences) {
    const focus = focusWord(sentence, known);
    for (let tried = 0; tried < ticked.length; tried += 1) {
      const part = ticked[(turn + tried) % ticked.length]!;
      const fits =
        part === "arrange"
          ? arrangeable(sentence)
          : part === "pinyin" || focus !== undefined;
      if (fits) {
        dealt[part].push({
          sentence,
          word: focus?.word ?? { text: "", reading: "" },
          at: focus?.at ?? -1,
        });
        turn = (turn + tried + 1) % ticked.length;
        break;
      }
    }
  }

  return dealt;
}

/** How many sentences share a box of words to choose from. */
export const gapGroup = 5;

/** Each part's rows, in millimetres. */
export const sentenceHeights = {
  box: 11,
  gaps: 14,
  arrange: 24,
  pinyin: 22.5,
  translate: 17,
  key: 6.5,
} as const;

/**
 * The least each part's rows can be drawn at, on a page drawn closer to take
 * the last of its part. Measured in the browser, where a gap's sentence and
 * its English hold 14 mm, a sentence to put in order 23 mm and one to write
 * from its pinyin 21.4 mm, each with the rule above it.
 */
export const sentenceLeast = {
  box: 10,
  gaps: 13,
  arrange: 23.2,
  pinyin: 21.6,
  translate: 15,
  key: 5.5,
  source: 4,
} as const;

/** The square a gap's word is written in, beside text printed at 5.4 mm. */
const gapSquare = 8;

/** The largest square a whole sentence is written in. */
const sentenceSquare = 9;

/** The room before a row's squares, for the number and its gap. */
const itemLead = 7.5;

/**
 * A sentence's characters to write in squares, and the punctuation that ends
 * it, which is printed after them. A comma inside the sentence takes a square
 * of its own, as it does on squared writing paper.
 */
export function written(text: string): {
  readonly characters: readonly string[];
  readonly ending: string;
} {
  const letters = Array.from(text);
  let end = letters.length;
  while (end > 0 && !han.test(letters[end - 1]!)) {
    end -= 1;
  }

  return {
    characters: letters.slice(0, end),
    ending: letters.slice(end).join(""),
  };
}

/**
 * A sentence's words out of order, the same order every time it is printed.
 * A shuffle that leaves the words as they were is turned once more, so no
 * item prints its own answer.
 */
export function shuffledWords(words: readonly string[]): readonly string[] {
  const shuffled = seededShuffle(words, words.join(" "));
  if (shuffled.every((word, at) => word === words[at])) {
    return [...shuffled.slice(1), shuffled[0]!];
  }

  return shuffled;
}

/** The size of square a sentence's characters fit in across this paper. */
export function squareFor(count: number, paper: WorksheetPaper): number {
  const room = pageRoom(paper).width - itemLead;

  return Math.min(sentenceSquare, Math.floor((room / count) * 10) / 10);
}

/** The words a box offers, in the order of their readings. */
function boxed(items: readonly SentenceItem[]): readonly string[] {
  return [...items]
    .sort((one, other) =>
      one.word.reading.localeCompare(other.word.reading, "en"),
    )
    .map((item) => item.word.text);
}

/** An answer's own words, underlined in the faint red. */
function mark(text: string): HTMLElement {
  return element("span", { className: "cb-wf-mark", text });
}

function text(value: string): Text {
  return document.createTextNode(value);
}

/** What drawing the sheet needs. */
export interface SentenceSheetProperties {
  /** Draws the squares a learner writes in. */
  readonly library: GlyphLibrary;
}

/** Draws sentence sheets. */
export class SentenceSheet {
  readonly #library: GlyphLibrary;

  constructor(properties: SentenceSheetProperties) {
    this.#library = properties.library;
  }

  /** Every page of the sheet, leaving out the parts not asked for. */
  pages(
    state: PartState<SentencePart>,
    choice: SentenceChoice,
    labels: SheetLabels,
  ): readonly HTMLElement[] {
    const gaps = state.gaps ? choice.gaps : [];
    const arrange = state.arrange ? choice.arrange : [];
    const pinyin = state.pinyin ? choice.pinyin : [];
    const translate = state.translate ? choice.translate : [];
    const gapStart = 1;
    const arrangeStart = gapStart + gaps.length;
    const pinyinStart = arrangeStart + arrange.length;
    const translateStart = pinyinStart + pinyin.length;

    const parts: FlowPart[] = [];
    let part = 0;
    if (gaps.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Fill the gaps",
        chinese: "选词填空",
        note: "Choose words from the box above each group, using each word once. Write one character in each square.",
        rows: this.#gapRows(gaps, gapStart),
      });
    }
    if (arrange.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Put in order",
        chinese: "连词成句",
        note: "Put the words in order to match the English meaning. Write the sentence in the squares, one character in each.",
        rows: sheetRows(
          arrange.map((item, at) =>
            this.#arrange(item, arrangeStart + at, state.paper),
          ),
          1,
          sentenceHeights.arrange,
          "cb-wjz-rule",
          sentenceLeast.arrange,
        ),
      });
    }
    if (pinyin.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Write the characters",
        chinese: "看拼音写句子",
        note: "Use the pinyin and English meaning to write each sentence in Chinese characters. Write one character in each square.",
        rows: sheetRows(
          pinyin.map((item, at) =>
            this.#pinyin(item, pinyinStart + at, state.paper),
          ),
          1,
          sentenceHeights.pinyin,
          "cb-wjz-rule",
          sentenceLeast.pinyin,
        ),
      });
    }
    if (translate.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Translate",
        chinese: "翻译",
        note: "Write each English sentence in Chinese on the line, using the word given. More than one translation can be correct.",
        rows: sheetRows(
          translate.map((item, at) =>
            this.#translate(item, translateStart + at),
          ),
          1,
          sentenceHeights.translate,
          "cb-wjz-rule",
          sentenceLeast.translate,
        ),
      });
    }
    if (state.key && parts.length > 0) {
      parts.push(
        ...this.#key([
          {
            title: "Fill the gaps",
            chinese: "选词填空",
            items: gaps,
            start: gapStart,
            marked: true,
          },
          {
            title: "Put in order",
            chinese: "连词成句",
            items: arrange,
            start: arrangeStart,
            marked: false,
          },
          {
            title: "Write the characters",
            chinese: "看拼音写句子",
            items: pinyin,
            start: pinyinStart,
            marked: false,
          },
          {
            title: "Translate",
            chinese: "翻译",
            items: translate,
            start: translateStart,
            marked: true,
            note: "The key gives one possible translation of each sentence. Other translations can be correct too.",
          },
        ]),
      );
    }

    return new SheetFlow({
      paper: state.paper,
      kind: "Sentences",
      kindChinese: "句子",
      parts,
      labels,
    }).pages();
  }

  #squares(count: number, size: number): SVGElement {
    return this.#library.row(
      size,
      Array.from({ length: count }, () => ({ kind: "empty" }) as const),
      undefined,
      "tracing",
      "tian",
    );
  }

  /** Each group of sentences under the box of its words. */
  #gapRows(items: readonly SentenceItem[], start: number): readonly FlowRow[] {
    const rows: FlowRow[] = [];
    for (let at = 0; at < items.length; at += gapGroup) {
      const group = items.slice(at, at + gapGroup);
      const box = element("div", {
        className: "cb-wf-row cb-wjz-box-row",
        children: [
          element("p", {
            className: "cb-wjz-box cb-wf-hz",
            attributes: { lang: "zh-Hans" },
            children: boxed(group).map((word) =>
              element("span", { text: word }),
            ),
          }),
        ],
      });
      rows.push({
        node: sized(box, { height: sentenceHeights.box }),
        height: sentenceHeights.box,
        least: sentenceLeast.box,
      });
      rows.push(
        ...sheetRows(
          group.map((item, index) => this.#gap(item, start + at + index)),
          1,
          sentenceHeights.gaps,
          "",
          sentenceLeast.gaps,
        ),
      );
    }

    return rows;
  }

  #gap(item: SentenceItem, number: number): HTMLElement {
    const { at } = item;
    const sentence = element("p", {
      className: "cb-wjz-sentence cb-wf-hz",
      attributes: { lang: "zh-Hans" },
      children: [
        text(item.sentence.text.slice(0, at)),
        this.#squares(Array.from(item.word.text).length, gapSquare),
        text(item.sentence.text.slice(at + item.word.text.length)),
      ],
    });

    return element("div", {
      className: "cb-wjz-item",
      children: [
        sheetNumber(number),
        sentence,
        element("span", { className: "cb-wf-en", text: item.sentence.english }),
      ],
    });
  }

  #arrange(
    item: SentenceItem,
    number: number,
    paper: WorksheetPaper,
  ): HTMLElement {
    const { characters, ending } = written(item.sentence.text);
    const pieces = element("p", {
      className: "cb-wjz-pieces cb-wf-hz",
      attributes: { lang: "zh-Hans" },
      children: shuffledWords(
        item.sentence.pieces.map((piece) => piece.text),
      ).map((word) => element("span", { text: word })),
    });

    return element("div", {
      className: "cb-wjz-item",
      children: [
        sheetNumber(number),
        pieces,
        this.#answerSquares(characters.length, ending, paper),
        element("span", { className: "cb-wf-en", text: item.sentence.english }),
      ],
    });
  }

  #pinyin(
    item: SentenceItem,
    number: number,
    paper: WorksheetPaper,
  ): HTMLElement {
    const { characters, ending } = written(item.sentence.text);

    return element("div", {
      className: "cb-wjz-item",
      children: [
        sheetNumber(number),
        element("p", {
          className: "cb-wjz-reading",
          text: item.sentence.pinyin,
          attributes: { lang: "zh-Latn-CN-pinyin" },
        }),
        this.#answerSquares(characters.length, ending, paper),
        element("span", { className: "cb-wf-en", text: item.sentence.english }),
      ],
    });
  }

  #translate(item: SentenceItem, number: number): HTMLElement {
    const english = element("p", {
      className: "cb-wjz-english",
      children: [
        text(item.sentence.english),
        element("span", {
          className: "cb-wjz-use",
          children: [sheetHanzi(item.word.text), sheetCue(item.word.reading)],
        }),
      ],
    });

    return element("div", {
      className: "cb-wjz-item",
      children: [
        sheetNumber(number),
        english,
        element("span", { className: "cb-wjz-line" }),
      ],
    });
  }

  #answerSquares(
    count: number,
    ending: string,
    paper: WorksheetPaper,
  ): HTMLElement {
    return element("span", {
      className: "cb-wjz-answer",
      children: [
        this.#squares(count, squareFor(count + 1, paper)),
        ...(ending === "" ? [] : [sheetHanzi(ending, "cb-wjz-ending")]),
      ],
    });
  }

  #key(
    sections: readonly {
      readonly title: string;
      readonly chinese: string;
      readonly items: readonly SentenceItem[];
      readonly start: number;
      readonly marked: boolean;
      readonly note?: string;
    }[],
  ): readonly FlowPart[] {
    const parts: FlowPart[] = [];
    let part = 0;
    for (const section of sections) {
      if (section.items.length === 0) {
        continue;
      }
      part += 1;
      parts.push({
        number: part,
        title: section.title,
        chinese: section.chinese,
        ...(section.note !== undefined && { note: section.note }),
        rows: sheetRows(
          section.items.map((item, at) =>
            this.#answer(item, section.start + at, section.marked),
          ),
          1,
          sentenceHeights.key,
          "",
          sentenceLeast.key,
        ),
        startsPage: parts.length === 0,
      });
    }
    const source = element("p", {
      className: "cb-wf-source",
      text: "Sentences, pinyin and English translations come from the Chinese Boost 例句 (example sentence) library. Some sentences have other correct word orders or translations.",
    });
    const last = parts.at(-1);
    if (last !== undefined) {
      parts[parts.length - 1] = {
        ...last,
        rows: [
          ...last.rows,
          {
            node: sized(source, { height: sentenceHeights.key }),
            height: sentenceHeights.key,
            least: sentenceLeast.source,
          },
        ],
      };
    }

    return parts;
  }

  #answer(item: SentenceItem, number: number, marked: boolean): HTMLElement {
    const sentence = item.sentence.text;
    const { at } = item;
    const children =
      marked && at >= 0
        ? [
            text(sentence.slice(0, at)),
            mark(item.word.text),
            text(sentence.slice(at + item.word.text.length)),
          ]
        : [text(sentence)];

    return element("div", {
      className: "cb-wf-key-item cb-wjz-key",
      children: [
        sheetNumber(number),
        element("span", {
          className: "cb-wf-hz",
          attributes: { lang: "zh-Hans" },
          children,
        }),
        element("span", {
          className: "cb-wf-en",
          text: item.sentence.pinyin,
          attributes: { lang: "zh-Latn-CN-pinyin" },
        }),
      ],
    });
  }
}
